import { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import {
  Bot,
  Edit2,
  MessagesSquare,
  Plus,
  Trash2,
  TriangleAlert,
} from "lucide-react";
import { useScriptsApi } from "../api/scriptsApi";
import { useAuth } from "../context/AuthContext";
import ScriptModal from "../modal/ScriptModal";
import ConfirmModal from "../modal/ConfirmModal";

const SEVERITY = {
  alta: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300",
  media: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300",
  baixa: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300",
};

const Scripts = () => {
  const api = useScriptsApi();
  const { user } = useAuth();
  const isAdmin = user?.role === "ADMIN";

  const [scripts, setScripts] = useState([]);
  const [variables, setVariables] = useState([]);
  const [editing, setEditing] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [testText, setTestText] = useState("");
  const [testResult, setTestResult] = useState(null);

  const load = useCallback(async () => {
    try {
      const data = await api.getAll();
      setScripts(data.scripts);
      setVariables(data.variables);
    } catch (error) {
      toast.error(error.message || "Não foi possível carregar os roteiros.");
    }
  }, [api]);

  useEffect(() => {
    load();
  }, [load]);

  const handleTest = async () => {
    if (!testText.trim()) return;
    try {
      setTestResult(await api.testMatch(testText));
    } catch (error) {
      toast.error(error.message || "Não foi possível testar.");
    }
  };

  const confirmDelete = async () => {
    try {
      await api.remove(toDelete);
      setToDelete(null);
      await load();
      toast.success("Roteiro excluído.");
    } catch (error) {
      toast.error(error.message || "Não foi possível excluir.");
    }
  };

  return (
    <div className="flex-1 overflow-y-auto bg-gray-50 p-6 transition-colors duration-300 dark:bg-gray-800">
      <div className="mx-auto max-w-5xl">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              Roteiros de Atendimento
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Um roteiro por área. A primeira mensagem do cliente decide qual
              encaixa, e o robô segue os passos até alguém do time assumir.
            </p>
          </div>

          {isAdmin && (
            <button
              onClick={() => { setEditing(null); setModalOpen(true); }}
              className="flex items-center gap-1.5 rounded-lg bg-green-500 px-4 py-2 text-sm text-white hover:bg-green-600"
            >
              <Plus className="h-4 w-4" />
              Novo roteiro
            </button>
          )}
        </div>

        {isAdmin && (
          <div className="mb-6 rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
            <p className="mb-2 flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
              <MessagesSquare className="h-4 w-4 text-blue-500" />
              Testar o encaixe
            </p>
            <div className="flex flex-wrap gap-2">
              <input
                value={testText}
                onChange={(event) => setTestText(event.target.value)}
                placeholder="Cole aqui uma mensagem real de cliente..."
                className="min-w-0 flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
              />
              <button
                onClick={handleTest}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800"
              >
                Testar
              </button>
            </div>

            {testResult && (
              <p className="mt-2 text-sm">
                {testResult.matched ? (
                  <span className="text-green-700 dark:text-green-400">
                    Casaria com <strong>{testResult.name}</strong> ({testResult.hits} termo
                    {testResult.hits > 1 ? "s" : ""}).
                  </span>
                ) : (
                  <span className="text-amber-700 dark:text-amber-400">
                    Nenhum roteiro casa com essa mensagem — o atendimento seguiria
                    manual.
                  </span>
                )}
              </p>
            )}
          </div>
        )}

        <div className="space-y-3">
          {scripts.length === 0 && (
            <div className="rounded-lg border border-dashed border-gray-300 p-10 text-center dark:border-gray-600">
              <Bot className="mx-auto mb-2 h-8 w-8 text-gray-400" />
              <p className="text-gray-600 dark:text-gray-400">
                Nenhum roteiro criado. Comece por uma área — trabalhista,
                previdenciário — com poucos passos.
              </p>
            </div>
          )}

          {scripts.map((script) => (
            <div
              key={script.id}
              className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-900"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-semibold text-gray-900 dark:text-gray-100">
                      {script.name}
                    </p>
                    <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${
                      script.enabled
                        ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300"
                        : "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400"
                    }`}>
                      {script.enabled ? "Ativo" : "Pausado"}
                    </span>
                    {script.activeConversations > 0 && (
                      <span className="rounded-full bg-blue-100 px-2.5 py-1 text-xs font-medium text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                        {script.activeConversations} conversa
                        {script.activeConversations > 1 ? "s" : ""} em andamento
                      </span>
                    )}
                    {script.warnings.length > 0 && (
                      <span className="flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-medium text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                        <TriangleAlert className="h-3 w-3" />
                        {script.warnings.length} aviso
                        {script.warnings.length > 1 ? "s" : ""} de conformidade
                      </span>
                    )}
                  </div>

                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {script.flow?.nodes?.length
                      ? `${script.flow.nodes.length} nós`
                      : `${script.steps.length} passo${script.steps.length > 1 ? "s" : ""} (formato antigo)`}
                    {script.keywords.length > 0 && ` · aciona com: ${script.keywords.join(", ")}`}
                  </p>

                  {script.warnings.length > 0 && (
                    <ul className="mt-2 space-y-1">
                      {script.warnings.map((warning, index) => (
                        <li key={index} className="flex flex-wrap items-center gap-2 text-xs">
                          <span className={`rounded-full px-2 py-0.5 font-medium ${SEVERITY[warning.severity]}`}>
                            {warning.label}
                          </span>
                          <span className="text-gray-600 dark:text-gray-400">
                            {warning.stepTitle}: “{warning.match}” — {warning.explanation}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {isAdmin && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => { setEditing(script); setModalOpen(true); }}
                      className="rounded p-1 text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20"
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => setToDelete(script.id)}
                      className="rounded p-1 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <ScriptModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSaved={load}
        script={editing}
      />

      <ConfirmModal
        isOpen={Boolean(toDelete)}
        title="Excluir roteiro"
        message="As conversas em andamento com este roteiro deixam de ser conduzidas pelo robô."
        onConfirm={confirmDelete}
        onCancel={() => setToDelete(null)}
      />
    </div>
  );
};

export default Scripts;
